import React, { useEffect, useState, useCallback } from "react";
import { X, Sparkles, Loader2, AlertTriangle, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { mauritiusDemoAiResult } from "@/lib/mauritiusDemoJourney";
import { demoMauritiusAiResult } from "@/lib/demoMauritius";

const CONF = { High: "bg-emerald-50 text-emerald-700", Medium: "bg-amber-50 text-amber-700", Low: "bg-slate-100 text-slate-500" };
const Section = ({ title, items }) => (!items?.length ? null : (
  <div className="mt-4">
    <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
    <ul className="space-y-1.5">
      {items.map((x, i) => <li key={i} className="flex gap-2 text-sm leading-relaxed text-[#334155]"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-[#3E7256]" />{x}</li>)}
    </ul>
  </div>
));

// Contextual Lodge AI analysis panel. Distinct from Ask Lodge (conversational).
export const LodgeAiDrawer = ({ open, onClose, orgId, actionType, context, title, demoSimulation = false, demoVariant }) => {
  const [status, setStatus] = useState("idle"); // idle|loading|success|error
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const run = useCallback(async () => {
    if (!orgId || !actionType || !context?.marketKey) { setStatus("error"); setErr("Contexte manquant."); return; }
    setStatus("loading"); setErr(""); setData(null);
    if (demoSimulation) {
      setData({
        result: demoVariant === "mauritius" ? demoMauritiusAiResult(context, actionType) : mauritiusDemoAiResult(context, actionType),
        isDemo: true,
        provenance: "Demo",
      });
      setStatus("success");
      return;
    }
    try {
      const { data } = await api.post(`/organizations/${orgId}/ai/run`, { action_type: actionType, context });
      setData(data); setStatus("success");
    } catch (e) {
      const payload = e.response?.data;
      setErr(formatApiError(payload?.message ?? payload?.detail, e.message)); setStatus("error");
    }
  }, [orgId, actionType, context, demoSimulation, demoVariant]);

  useEffect(() => { if (open) run(); /* re-run when target changes */ }, [open, run]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const r = data?.result;
  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} data-testid="lodge-ai-overlay" />}
      <aside
        role="dialog" aria-modal="true" aria-label="Lodge AI"
        data-testid="lodge-ai-drawer"
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#ECF3EC] text-[#3E7256]"><Sparkles size={16} /></span>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#3E7256]">Lodge AI</p>
              <p className="text-sm font-semibold text-[#1E3D2B]">{title || "Analyse"}</p>
            </div>
          </div>
          <button type="button" data-testid="lodge-ai-close" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {status === "loading" && (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400" data-testid="lodge-ai-loading">
              <Loader2 size={26} className="animate-spin text-[#3E7256]" />
              <p className="mt-3 text-sm">Lodge AI analyse les données…</p>
            </div>
          )}
          {status === "error" && (
            <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="lodge-ai-error">
              <AlertTriangle size={26} className="text-amber-500" />
              <p className="mt-3 text-sm font-medium text-slate-600">{err || "Lodge AI est momentanément indisponible."}</p>
              <div className="mt-4 flex items-center gap-2">
                <button type="button" onClick={run} className="rounded-lg bg-[#1E3D2B] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2C5540]">Réessayer</button>
                <button type="button" onClick={onClose} data-testid="lodge-ai-error-close" className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Fermer</button>
              </div>
            </div>
          )}
          {status === "success" && r && (
            <div data-testid="lodge-ai-result">
              <div className="flex flex-wrap items-center gap-2">
                {data.isDemo && <span data-testid="lodge-ai-demo" className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">Analyse de démonstration</span>}
                <span data-testid="lodge-ai-provenance" className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-600">{data.provenance}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${CONF[r.confidence] || CONF.Low}`}>{r.confidence} confidence</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-[#1E3D2B]" data-testid="lodge-ai-summary">{r.summary}</p>
              <Section title="Signaux clés" items={r.keySignals} />
              <Section title="Interprétation" items={r.interpretation} />
              <Section title="Actions recommandées" items={r.recommendedActions} />
              <Section title="Points de vigilance" items={r.caveats} />
              {r.provenance?.length > 0 && (
                <div className="mt-4 flex items-start gap-1.5 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                  <Info size={13} className="mt-0.5 shrink-0" /> Sources : {r.provenance.join(", ")}. Recommandations à valider avant toute action.
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default LodgeAiDrawer;
